<script>
/* eslint-disable vue/require-v-for-key */
import cloneDeep from "lodash/cloneDeep";
import StartEndTimes from "./StartEndTimes.vue";
import { format } from "../libs/date.format.js";
import CustomCheckbox from "./CustomCheckbox.vue";
import datesMixins from "../mixins/datesMixins";
import RadioInputGroup from "@/components/forms/RadioInputGroup";

export default {
    name: "NewHolidays",
    emits: ["updated"],
    components: {
        CustomCheckbox,
        StartEndTimes,
        RadioInputGroup,
    },
    mixins: [datesMixins],
    props: {
        defaultHolidays: {
            type: Array,
            default: () => [],
        },
        startDate: {
            type: [String, Date],
        },
        endDate: {
            type: [String, Date],
        },
        existingHolidays: {
            type: Array,
            default: () => [],
        },
        showFormatted: {
            type: Boolean,
            default: false,
        },
        startHolidayLabel: {
            type: String,
            default: "",
        },
        endHolidayLabel: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            newHoliday: {
                start: null,
                end: null,
                oneDay: false,
            },
            holidays: [],

            showSuggestedHolidays: false,
            holidayType: "new",
            suggestedHoliday: "",
        };
    },
    methods: {

        selectSuggestedHoliday(holiday) {
            this.showSuggestedHolidays = false;
            const newHoliday = {
                start: { date: holiday.start, plain: holiday.start },
                end: { date: holiday.end, plain: holiday.end },
                oneDay: holiday.one_day === "Y",
            };

            this.holidays.push(newHoliday);

            this.emitUpdate();
        },

        returnFormattedHoliday(holiday, oneDay = false) {
            const startPlain = holiday.start?.plain || null;
            const endPlain = holiday.end?.plain || null;
            const start = Date.parse(startPlain);
            const end = Date.parse(holiday.end?.plain);
            const startWeekday = this.getWeekdayFromDate(new Date(startPlain));
            const endWeekday = this.getWeekdayFromDate(new Date(endPlain));
            if (oneDay && !Number.isNaN(start)) {
                return ` ${startWeekday} ${format(new Date(startPlain), "dS mmmm yyyy")}`;
            }
            if (oneDay && Number.isNaN(start)) {
                return "";
            }

            if (!Number.isNaN(start) && !Number.isNaN(end)) {
                return ` ${startWeekday} ${format(new Date(startPlain), "dS mmmm yyyy")} - ${endWeekday} ${format(new Date(endPlain), "dS mmmm yyyy")}`;
            }
            return "";
        },

        returnFormattedExistingHoliday(holiday, oneDay) {
            const holidayStart = holiday.start || null;
            const holidayEnd = holiday.end || null;
            const start = Date.parse(holidayStart);
            const end = Date.parse(holidayEnd);

            if (oneDay === "Y" && !Number.isNaN(start)) {
                return format(new Date(holidayStart), "dS mmmm yyyy");
            }
            if (oneDay === "Y" && Number.isNaN(start)) {
                return "";
            }

            if (!Number.isNaN(start) && !Number.isNaN(end)) {
                return `${format(new Date(holidayStart), "dS mmmm yyyy")} - ${format(new Date(holidayEnd), "dS mmmm yyyy")}`;
            }
            return "";
        },

        removeHoliday(holidayIdx) {
            this.holidays = this.holidays.filter((h) => h.idx !== holidayIdx);
            this.emitUpdate();
        },

        setCheckbox() {
            this.newHoliday.oneDay = !this.newHoliday.oneDay;

            this.pushNewHoliday();
        },

        setNewHolidays(dates) {
            this.newHoliday.start = dates.start;
            this.newHoliday.end = dates.end;

            this.pushNewHoliday();
        },

        emitUpdate() {
            // console.log(this.holidays.reverse().reverse());
            this.$emit("updated", this.holidays.reverse().reverse());
        },

        resetAndEmitHoliday() {
            this.newHoliday.start = null;
            this.newHoliday.end = null;
            this.newHoliday.oneDay = false;

            this.$refs.startEndTime.resetDates();
            this.emitUpdate();
        },

        pushNewHoliday() {
            const start = Date.parse(this.newHoliday.start?.plain);
            const end = Date.parse(this.newHoliday.end?.plain);

            if (!Number.isNaN(start) && !Number.isNaN(end) && (start !== end)) {
                this.holidays.push({ ...this.newHoliday });
                this.resetAndEmitHoliday();
            }

            if (!Number.isNaN(start) && this.newHoliday.oneDay === true) {
                this.holidays.push({ ...this.newHoliday });
                this.resetAndEmitHoliday();
            }
        },
    },
    computed: {
        getHolidays() {
            return this.holidays.slice().reverse();
        },
    },
    watch: {
        defaultHolidays: {
            deep: true,
            handler() {
                this.holidays = cloneDeep(this.defaultHolidays);
            },
        },

    },
    mounted() {
        if (this.defaultHolidays.length > 0) {
            this.holidays = cloneDeep(this.defaultHolidays);
        }
    },
};
</script>

<template>
    <div v-if="existingHolidays.length > 0" class="my-3">

        <div v-if="existingHolidays.length > 0" class="d-flex flex-column">
            <div class="d-flex flex-column">
                <RadioInputGroup
                    label="title"
                    v-model="holidayType"
                    :selected="holidayType"
                    :list="[
                        {
                            title: 'Add new holiday dates',
                            value: 'new',
                        },
                        {
                            title: 'Use pre-existing holiday dates',
                            value: 'existing',
                        },
                    ]"
                />
            </div>
        </div>

        <div v-if="existingHolidays.length > 0 && holidayType === 'existing'">

            <div class="mt-3 d-none flex-column" :class="{ 'd-flex': existingHolidays.length > 0 }">
                <div class="position-relative">
                    <div v-show="holidayType === 'existing'" class="position-relative">
                        <input
                            @click="showSuggestedHolidays = !showSuggestedHolidays"
                            v-model="suggestedHoliday"
                            class="w-100 text"
                            type="text"
                            placeholder="Choose a saved holiday"
                            readonly
                        />
                        <div v-show="showSuggestedHolidays" class="arrow">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                class="bi bi-chevron-up"
                                viewBox="0 0 16 16"
                            >
                                <path fill-rule="evenodd"
                                      d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
                            </svg>
                        </div>
                        <div v-show="!showSuggestedHolidays" class="arrow">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                class="bi bi-chevron-down"
                                viewBox="0 0 16 16"
                            >
                                <path fill-rule="evenodd"
                                      d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                            </svg>
                        </div>
                    </div>

                    <div v-show="showSuggestedHolidays" class="added-classes-container holidays">
                        <div
                            @click="selectSuggestedHoliday(single)"
                            class="cursor-pointer ml-1 added-item"
                            v-for="single in existingHolidays"
                        >
                            <span class="holiday-title">{{
                                returnFormattedExistingHoliday(single, single.one_day)
                            }}</span>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>

    <div v-if="holidayType === 'new'">
        <StartEndTimes
            @change="setNewHolidays($event)"
            class="mt-4"
            parent-page="mp"
            :index="3"
            ref="startEndTime"
            :required="false"
            :disabled-dates="{
                to: new Date(startDate || null),
                from: new Date(endDate || null),
            }"
            :options="{
                startDateLabel: startHolidayLabel,
                endDateLabel: endHolidayLabel,
            }"
            :disabled-end-date="newHoliday.oneDay"
        />

        <div class="mb-3 mt-3">
            <CustomCheckbox @checkbox-click="setCheckbox" :checked="newHoliday.oneDay"
                            dataTest="checkbox-holiday-oneday">
                One day holiday
            </CustomCheckbox>
        </div>
    </div>

    <div
        :key="holidayIdx"
        v-for="(holiday, holidayIdx) in getHolidays"
        :class="{ 'remove-border': holiday.remove }"
        class="my-3 holiday-block"
    >
        <div class="holiday-title">
            {{ returnFormattedHoliday(holiday, holiday.oneDay) }}
        </div>
        <div class="d-flex flex-row cursor-pointer">

            <div @click="removeHoliday(holiday.idx)" class="ml-2 mr-2 cursor-pointer action-item remove">
                Delete
            </div>
        </div>

    </div>
</template>

<style>
.holidays .added-item {
    margin-top: 8px;
    height: 30px;
}

.holiday-block {
    padding: 1rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background: #FFFFFF;
    box-shadow: 0px 0px 8px rgb(0 0 0 / 15%);
    box-sizing: border-box;
    border-radius: 15px;
    margin-bottom: 1rem;
}

.holiday-title {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    color: #272529;
}

.added-classes-container.holidays {
    top: 47px;
}
</style>
